
import { defineComponent, computed, ref } from "vue";
import Modal from "../Components/Modal.vue";
import { useStore } from "../../stores";
import { GameItem } from "../../types/game-item";
import { GameTypeEnum } from "../../types/game-type-enum";
import { WagerTypeEnum } from "../../types/wager-type-enum";
import appRestService from "../../protocol/rest-service";
import AppConfig from "@/config/app-config";
import gameApplication from "../../game/game-app";
import { AbstractPlayer } from "@/types/abstract-player";
import websocketHandler from "../../protocol/websocket-handler";

export default defineComponent({
  name: "InvitationConfirmModal",

  data: () => ({
    cryptoValue: Number,
  }),

  props: {
    inviteePlayerName: { type: String, required: true },
  },
  
  setup() {
    const store = useStore();
    const playerList = computed(() => store.state.playerList);
    const gameTypesArray = [];

    for (let gameTypeEnum in GameTypeEnum) {
      // check for numbers here because TypeScript returns redundant intergers
      if (Number.isNaN(Number(gameTypeEnum))) {
        gameTypesArray.push(gameTypeEnum);
      }
    }

    const gameTypes = ref(gameTypesArray);

    // determine if the dropdown is selected and also used for the selected value
    const gameTypeSelected = ref(0);
  
    // determines if the dropdown is selected
    const wagerSelected = ref(0);

    // these are used for the selected values
    const shotTimeSelected = ref(30);
    const wagerNetworkSelected = ref("");

    return { playerList, gameTypes, gameTypeSelected, shotTimeSelected, wagerNetworkSelected, wagerSelected };
  },

  methods: {
    /**
     * This get the crypto prices from the Coin Gekio REST API.
     * The prices are written to a JSON file.
     */
    async getCryptoPrices(): Promise<void> {
      if (gameApplication.cryptoPricesJSON === undefined) {
        const res = await appRestService.get(AppConfig.COIN_GEKIO_REST_API);

        if (res.data) {
          gameApplication.cryptoPricesJSON = websocketHandler.cleanJSON(res.data);
          this.getWagerAmounts();
        }
      } else {
        this.getWagerAmounts();
      }
    },
 
    /**
     * This get the wager amounts for the dropdown list.
     * The prices in the list depend on the network selected.
     */
    getWagerAmounts(): void {
      const cryptoPrice = gameApplication.cryptoPricesJSON;
      const round = (number, decimalPlaces) => Number(Math.round(Number(number + "e" + decimalPlaces)) + "e" + decimalPlaces * -1);
      const decimals = 4;

      // this indicates that the network has been selected
      this.networkSelected = 1;
      
      if (this.wagerNetworkSelected != '') {
        switch (this.wagerNetworkSelected) {
        case 'ETH':
          this.cryptoValue = round(1 / cryptoPrice.ethereum.usd, decimals);
          break;
        case 'AVAX':
          this.cryptoValue = round(1 / cryptoPrice['avalanche-2'].usd, decimals);
          break;
        case 'BNB':
          this.cryptoValue = round(1 / cryptoPrice.binancecoin.usd, decimals);
          break;
        case 'FTM':
          this.cryptoValue = round(1 / cryptoPrice.fantom.usd, decimals);
          break;
        case 'MATIC':  
          this.cryptoValue = round(1 / cryptoPrice['matic-network'].usd, decimals);
          break;
        }
      }
    },

    onConfirm() {
      if (GameTypeEnum[GameTypeEnum[this.gameTypeSelected]] === GameTypeEnum.Wager 
            && gameApplication.wagerMode === WagerTypeEnum.Wallet) {         
        gameApplication.initiateWagerGame(
          this.getInviteeOpponentPlayer(),
          this.gameTypeSelected, 
          this.shotTimeSelected,
          this.wagerNetworkSelected, 
          this.wagerSelected * this.cryptoValue,
        );
      } else {
        const newGame: GameItem = {
          player2: this.getInviteeOpponentPlayer(),
          gameType: this.gameTypeSelected,
          timeLimit: this.shotTimeSelected,
          network: '',
          wager: 0,
        };

        gameApplication.createNewGameRequest(newGame);
      }

      this.$emit('close');
    },

    getInviteeOpponentPlayer(): AbstractPlayer {
      const opponentPlayer: AbstractPlayer = this.playerList.find(
        (p) => p.displayName === this.inviteePlayerName
      );
      return opponentPlayer;
    },

    getGameTypes(): any {
      let newGameTypes = this.gameTypes;
      
      if (!gameApplication.getPlayer().player.guest && !this.getInviteeOpponentPlayer().guest) {
        newGameTypes.splice(3, 1); // remove tournament    
      } else {
        // remove everything except friendly and wager games 
        newGameTypes.splice(2, 3);
      }

      return newGameTypes;
    },
    
    onClose(): void {
      this.$emit('close');
    },
  },

  components: { Modal },
});
